import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface VideoDownloadTask {
  nodeId: string;
  nodeName: string;
  targetId: string;
  accessToken: string;
  selectedTab: string;
  timestamp: number;
}

export interface FailedTask {
  nodeId: string;
  nodeName: string;
  targetId: string;
  errorMessage: string;
  timestamp: number;
}

export interface VideoDownloadState {
  isDownloading: boolean;
  downloadingNodeId: string | null;
  downloadingNodeName: string | null;
  downloadingTargetId: string | null;
  downloadProgress: number;
  downloadedBytes: number;
  totalBytes: number;
  downloadStage: "connecting" | "downloading" | "extracting" | "completed" | "failed" | "";
  isExtracting: boolean;
  isWidgetMinimized: boolean;
  currentTask: VideoDownloadTask | null;
  downloadQueue: VideoDownloadTask[];
  failedTasks: Record<string, FailedTask>;
}

const initialState: VideoDownloadState = {
  isDownloading: false,
  downloadingNodeId: null,
  downloadingNodeName: null,
  downloadingTargetId: null,
  downloadProgress: 0,
  downloadedBytes: 0,
  totalBytes: 0,
  downloadStage: "",
  isExtracting: false,
  isWidgetMinimized: false,
  currentTask: null,
  downloadQueue: [],
  failedTasks: {},
};

const videoDownloadSlice = createSlice({
  name: "videoDownload",
  initialState,
  reducers: {
    enqueueVideoDownload: (
      state,
      action: PayloadAction<Omit<VideoDownloadTask, "timestamp">>,
    ) => {
      const task: VideoDownloadTask = {
        ...action.payload,
        timestamp: Date.now(),
      };

      // Clear any previous failure state for this node
      delete state.failedTasks[task.nodeId];
      if (task.targetId) {
        delete state.failedTasks[task.targetId];
      }

      // Avoid duplicate queuing if already downloading or in queue
      const isCurrentActive =
        (state.currentTask?.nodeId &&
          (state.currentTask.nodeId === task.nodeId ||
            state.currentTask.nodeId === task.targetId)) ||
        (state.currentTask?.targetId &&
          (state.currentTask.targetId === task.nodeId ||
            state.currentTask.targetId === task.targetId));

      if (isCurrentActive) {
        return;
      }

      const isAlreadyQueued = state.downloadQueue.some(
        (t) =>
          (t.nodeId && (t.nodeId === task.nodeId || t.nodeId === task.targetId)) ||
          (t.targetId && (t.targetId === task.nodeId || t.targetId === task.targetId)),
      );

      if (isAlreadyQueued) {
        return;
      }

      // Max limit 5 total (1 active + 4 in queue)
      const totalActiveAndQueued =
        (state.currentTask ? 1 : 0) + state.downloadQueue.length;
      if (totalActiveAndQueued >= 5) {
        return;
      }

      // If nothing is currently downloading, start immediately
      if (!state.currentTask && !state.isDownloading) {
        state.currentTask = task;
        state.downloadingNodeId = task.nodeId;
        state.downloadingNodeName = task.nodeName;
        state.downloadingTargetId = task.targetId;
        state.isDownloading = true;
        state.downloadProgress = 0;
        state.downloadedBytes = 0;
        state.totalBytes = 0;
        state.downloadStage = "connecting";
        state.isExtracting = false;
        state.isWidgetMinimized = false;
      } else {
        state.downloadQueue.push(task);
      }
    },
    startVideoDownload: (
      state,
      action: PayloadAction<Omit<VideoDownloadTask, "timestamp">>,
    ) => {
      const task: VideoDownloadTask = {
        ...action.payload,
        timestamp: Date.now(),
      };

      delete state.failedTasks[task.nodeId];
      if (task.targetId) {
        delete state.failedTasks[task.targetId];
      }

      if (state.currentTask?.nodeId === task.nodeId) {
        return;
      }
      if (state.downloadQueue.some((t) => t.nodeId === task.nodeId)) {
        return;
      }

      const totalActiveAndQueued =
        (state.currentTask ? 1 : 0) + state.downloadQueue.length;
      if (totalActiveAndQueued >= 5) {
        return;
      }

      if (!state.currentTask && !state.isDownloading) {
        state.currentTask = task;
        state.downloadingNodeId = task.nodeId;
        state.downloadingNodeName = task.nodeName;
        state.downloadingTargetId = task.targetId;
        state.isDownloading = true;
        state.downloadProgress = 0;
        state.downloadedBytes = 0;
        state.totalBytes = 0;
        state.downloadStage = "connecting";
        state.isExtracting = false;
        state.isWidgetMinimized = false;
      }
    },
    updateDownloadProgress: (
      state,
      action: PayloadAction<{
        nodeId?: string;
        targetId?: string;
        progress: number;
        downloadedBytes?: number;
        totalBytes?: number;
        stage?:
          | "connecting"
          | "downloading"
          | "extracting"
          | "completed"
          | "failed"
          | "";
        isExtracting?: boolean;
      }>,
    ) => {
      const {
        nodeId,
        targetId,
        progress,
        downloadedBytes,
        totalBytes,
        stage,
        isExtracting,
      } = action.payload;

      // Discard all updates if not currently downloading
      if (!state.isDownloading || !state.downloadingNodeId) {
        return;
      }

      // Discard stale updates from a previous or cancelled download task
      if (
        nodeId &&
        nodeId !== state.downloadingNodeId &&
        nodeId !== state.downloadingTargetId
      ) {
        return;
      }
      if (
        targetId &&
        targetId !== state.downloadingTargetId &&
        targetId !== state.downloadingNodeId
      ) {
        return;
      }

      if (stage !== undefined) {
        state.downloadStage = stage;
      }
      if (isExtracting !== undefined) {
        state.isExtracting = isExtracting;
      }

      // If connecting, starting new download, or explicitly resetting
      if (stage === "connecting" || downloadedBytes === 0 || progress === 0) {
        state.downloadProgress = Math.max(0, Math.min(100, Math.round(progress || 0)));
        state.downloadedBytes = downloadedBytes ?? 0;
        if (totalBytes !== undefined && totalBytes > 0) {
          state.totalBytes = totalBytes;
        } else if (downloadedBytes === 0 && progress === 0) {
          state.totalBytes = 0;
        }
        return;
      }

      // During active downloading
      if (totalBytes !== undefined && totalBytes > 0) {
        state.totalBytes = totalBytes;
      }

      if (progress !== undefined) {
        const roundedProgress = Math.min(100, Math.round(progress));
        state.downloadProgress = Math.max(state.downloadProgress, roundedProgress);
      }

      if (downloadedBytes !== undefined) {
        const cappedBytes =
          state.totalBytes > 0
            ? Math.min(downloadedBytes, state.totalBytes)
            : downloadedBytes;
        state.downloadedBytes = Math.max(state.downloadedBytes, cappedBytes);
      }
    },
    setDownloadWidgetMinimized: (state, action: PayloadAction<boolean>) => {
      state.isWidgetMinimized = action.payload;
    },
    removeFromQueue: (state, action: PayloadAction<string>) => {
      state.downloadQueue = state.downloadQueue.filter(
        (task) =>
          task.nodeId !== action.payload && task.targetId !== action.payload,
      );
    },
    finishCurrentAndStartNext: (state) => {
      if (state.downloadQueue.length > 0) {
        const nextTask = state.downloadQueue.shift()!;
        state.currentTask = nextTask;
        state.downloadingNodeId = nextTask.nodeId;
        state.downloadingNodeName = nextTask.nodeName;
        state.downloadingTargetId = nextTask.targetId;
        state.isDownloading = true;
        state.downloadProgress = 0;
        state.downloadedBytes = 0;
        state.totalBytes = 0;
        state.downloadStage = "connecting";
        state.isExtracting = false;
      } else {
        state.currentTask = null;
        state.isDownloading = false;
        state.downloadingNodeId = null;
        state.downloadingNodeName = null;
        state.downloadingTargetId = null;
        state.downloadProgress = 0;
        state.downloadedBytes = 0;
        state.totalBytes = 0;
        state.downloadStage = "";
        state.isExtracting = false;
      }
    },
    completeVideoDownload: (state) => {
      state.downloadProgress = 100;
      state.downloadStage = "completed";
    },
    markTaskAsFailed: (
      state,
      action: PayloadAction<{
        nodeId: string;
        nodeName: string;
        targetId: string;
        errorMessage: string;
      }>,
    ) => {
      const { nodeId, nodeName, targetId, errorMessage } = action.payload;
      const failure: FailedTask = {
        nodeId,
        nodeName,
        targetId,
        errorMessage,
        timestamp: Date.now(),
      };
      state.failedTasks[nodeId] = failure;
      if (targetId) {
        state.failedTasks[targetId] = failure;
      }
      state.downloadStage = "failed";
    },
    clearFailedTask: (state, action: PayloadAction<string>) => {
      const keyToRemove = action.payload;
      const taskToRemove = state.failedTasks[keyToRemove];
      delete state.failedTasks[keyToRemove];
      if (taskToRemove) {
        if (taskToRemove.nodeId) delete state.failedTasks[taskToRemove.nodeId];
        if (taskToRemove.targetId) delete state.failedTasks[taskToRemove.targetId];
      }
    },
    clearAllFailedTasks: (state) => {
      state.failedTasks = {};
    },
    cancelCurrentDownload: (
      state,
      action?: PayloadAction<{ nodeId?: string } | undefined>,
    ) => {
      const targetNodeId = action?.payload?.nodeId;
      if (
        targetNodeId &&
        state.currentTask?.nodeId !== targetNodeId &&
        state.currentTask?.targetId !== targetNodeId
      ) {
        state.downloadQueue = state.downloadQueue.filter(
          (task) =>
            task.nodeId !== targetNodeId && task.targetId !== targetNodeId,
        );
        return;
      }

      state.isDownloading = false;
      state.downloadingNodeId = null;
      state.downloadingNodeName = null;
      state.downloadingTargetId = null;
      state.downloadProgress = 0;
      state.downloadedBytes = 0;
      state.totalBytes = 0;
      state.downloadStage = "";
      state.isExtracting = false;
      state.currentTask = null;

      if (state.downloadQueue.length > 0) {
        const nextTask = state.downloadQueue.shift()!;
        state.currentTask = nextTask;
        state.downloadingNodeId = nextTask.nodeId;
        state.downloadingNodeName = nextTask.nodeName;
        state.downloadingTargetId = nextTask.targetId;
        state.isDownloading = true;
        state.downloadProgress = 0;
        state.downloadedBytes = 0;
        state.totalBytes = 0;
        state.downloadStage = "connecting";
        state.isExtracting = false;
      }
    },
    resetVideoDownload: (state) => {
      state.isDownloading = false;
      state.downloadingNodeId = null;
      state.downloadingNodeName = null;
      state.downloadingTargetId = null;
      state.downloadProgress = 0;
      state.downloadedBytes = 0;
      state.totalBytes = 0;
      state.downloadStage = "";
      state.isExtracting = false;
      state.currentTask = null;
      state.downloadQueue = [];
      state.failedTasks = {};
    },
  },
});

export const {
  enqueueVideoDownload,
  startVideoDownload,
  updateDownloadProgress,
  setDownloadWidgetMinimized,
  removeFromQueue,
  finishCurrentAndStartNext,
  completeVideoDownload,
  markTaskAsFailed,
  clearFailedTask,
  clearAllFailedTasks,
  cancelCurrentDownload,
  resetVideoDownload,
} = videoDownloadSlice.actions;

export default videoDownloadSlice.reducer;
